import { useEffect, useMemo, useState } from "react";
import { CalendarCheck, Users, Table2, Percent, Clock3, Activity, Settings2, Gift } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, fmtErr } from "../../lib/api";

const STATUS_STYLES = {
  confirmed: "bg-emerald-400/10 text-emerald-300 border-emerald-400/30",
  seated: "bg-blue-400/10 text-blue-300 border-blue-400/30",
  completed: "bg-zinc-400/10 text-zinc-300 border-zinc-400/30",
  cancelled: "bg-red-400/10 text-red-300 border-red-400/30",
  no_show: "bg-amber-400/10 text-amber-300 border-amber-400/30",
  pending: "bg-orange-400/10 text-orange-300 border-orange-400/30",
};

const WIDGETS = [
  ["show_stats", "Stat cards (covers, occupancy, tables)"],
  ["show_upcoming", "Upcoming service panel"],
  ["show_activity", "Recent activity feed"],
  ["show_chart", "7-day bookings chart"],
];

export default function Overview({ restaurant, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [customizing, setCustomizing] = useState(false);
  const [layout, setLayout] = useState({
    show_stats: true, show_upcoming: true, show_activity: true, show_chart: true,
    ...(restaurant.hub_layout || {}),
  });

  useEffect(() => {
    api.get(`/restaurants/${restaurant.id}/stats`, { params: { date: today } }).then((r) => setStats(r.data)).catch(() => {});
    api.get(`/restaurants/${restaurant.id}/bookings`).then((r) => setBookings(r.data)).catch(() => {});
  }, [restaurant.id, today]);

  const saveLayout = async () => {
    try {
      await api.put(`/restaurants/${restaurant.id}`, { hub_layout: layout });
      toast.success("Hub layout saved");
      setCustomizing(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const chartData = useMemo(() => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      days.push({
        day: d.toLocaleDateString(undefined, { weekday: "short" }),
        bookings: bookings.filter((b) => b.date === key && b.status !== "cancelled").length,
      });
    }
    return days;
  }, [bookings]);

  const cards = [
    { icon: Users, label: "Covers booked today", value: stats ? stats.covers_booked : "–", testid: "stat-covers-booked" },
    { icon: CalendarCheck, label: "Covers seated", value: stats ? stats.covers_seated : "–", testid: "stat-covers-seated" },
    { icon: Percent, label: "Occupancy", value: stats ? `${stats.occupancy}%` : "–", testid: "stat-occupancy" },
    { icon: Table2, label: "Tables", value: stats ? stats.table_count : "–", testid: "stat-tables" },
    { icon: Gift, label: "Voucher interest", value: stats ? stats.voucher_clicks_month : "–", sub: stats ? `${stats.voucher_clicks_total} clicks all time` : "", testid: "stat-voucher-clicks" },
    { icon: Gift, label: "Voucher sales", value: stats ? `£${(stats.voucher_sales_total || 0).toFixed(0)}` : "–", sub: stats ? `£${(stats.voucher_outstanding || 0).toFixed(0)} outstanding` : "", testid: "stat-voucher-sales" },
  ];

  return (
    <div data-testid="overview-tab">
      <div className="flex justify-end mb-4">
        <Button data-testid="customize-hub-btn" size="sm" variant="outline" className="rounded-full" onClick={() => setCustomizing(true)}>
          <Settings2 className="w-4 h-4 mr-2" /> Customise hub
        </Button>
      </div>

      {layout.show_stats && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-4">
          {cards.map((c) => (
            <div key={c.label} data-testid={c.testid}
              className="rounded-2xl border border-border/60 bg-card p-4 sm:p-5 hover:-translate-y-[1px] transition-transform duration-200">
              <div className="flex items-center justify-between">
                <p className="text-xs tracking-[0.15em] uppercase text-muted-foreground">{c.label}</p>
                <c.icon className="w-4 h-4 text-primary" />
              </div>
              <p className="font-outfit font-semibold text-3xl mt-3">{c.value}</p>
              {c.sub && <p className="text-[11px] text-muted-foreground mt-1">{c.sub}</p>}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {layout.show_upcoming && (
          <div className="rounded-2xl border border-border/60 bg-card p-6">
            <h3 className="font-outfit font-medium text-lg flex items-center gap-2">
              <Clock3 className="w-5 h-5 text-primary" /> Upcoming service
            </h3>
            <div className="mt-4 space-y-2.5">
              {(stats?.upcoming || []).map((b) => (
                <div key={b.id} data-testid={`upcoming-${b.id}`}
                  className="flex items-center justify-between rounded-xl border border-border/40 px-4 py-3">
                  <div>
                    <p className="text-sm font-medium">{b.name}</p>
                    <p className="text-xs text-muted-foreground">Party of {b.party_size} · Table {b.table_label}</p>
                  </div>
                  <span className="font-outfit font-semibold text-lg text-primary">{b.time}</span>
                </div>
              ))}
              {stats && stats.upcoming.length === 0 && (
                <p className="text-sm text-muted-foreground py-4" data-testid="no-upcoming-msg">No confirmed bookings left today.</p>
              )}
            </div>
          </div>
        )}

        {layout.show_activity && (
          <div className="rounded-2xl border border-border/60 bg-card p-6">
            <h3 className="font-outfit font-medium text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" /> Recent activity
            </h3>
            <div className="mt-4 space-y-2.5">
              {(stats?.recent || []).map((b) => (
                <div key={b.id} data-testid={`activity-${b.id}`} className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm truncate">{b.name} · {b.date} {b.time}</p>
                    <p className="text-[10px] text-muted-foreground">
                      booked {b.created_at ? new Date(b.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                    </p>
                  </div>
                  <Badge variant="outline" className={`${STATUS_STYLES[b.status]} shrink-0`}>{b.status.replace("_", " ")}</Badge>
                </div>
              ))}
              {stats && stats.recent.length === 0 && (
                <p className="text-sm text-muted-foreground py-4" data-testid="no-activity-msg">No activity yet.</p>
              )}
            </div>
          </div>
        )}
      </div>

      {layout.show_chart && (
        <div className="mt-6 rounded-2xl border border-border/60 bg-card p-6">
          <h3 className="font-outfit font-medium text-lg">Last 7 days</h3>
          <div className="h-56 mt-4" data-testid="bookings-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <XAxis dataKey="day" stroke="#52525b" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#52525b" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
                <Area type="monotone" dataKey="bookings" stroke="#D97757" fill="#D97757" fillOpacity={0.15} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <Dialog open={customizing} onOpenChange={setCustomizing}>
        <DialogContent className="bg-card border-border/60">
          <DialogHeader><DialogTitle className="font-outfit">Customise your hub</DialogTitle></DialogHeader>
          <div className="space-y-5 mt-2" data-testid="hub-customize-panel">
            {WIDGETS.map(([key, label]) => (
              <div key={key} className="flex items-center justify-between">
                <Label className="text-sm">{label}</Label>
                <Switch data-testid={`widget-${key}`} checked={layout[key]}
                  onCheckedChange={(v) => setLayout({ ...layout, [key]: v })} />
              </div>
            ))}
            <Button data-testid="save-hub-layout-btn" onClick={saveLayout} className="w-full rounded-full">Save layout</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
